import { useState, useEffect } from 'react';
import { FaArrowLeft, FaEnvelope, FaWhatsapp, FaCheck, FaExclamationTriangle, FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';

interface MessageDetails {
    messageId: string;
    notificationId: string;
    notificationType: string;
    scheduledDate: string;
    operation: string;
    recipient: string;
    recipientType: string;
    subject: string;
    attachments: string[];
    attachmentCount: number;
    isGrouped: boolean;
    groupedItemsCount: number;
    status: string;
    externalId?: string;
    sentAt?: string;
    deliveredAt?: string;
    createdAt?: string;
    updatedAt?: string;
    errorReason?: string;
}

interface MessageDetailsProps {
    messageId: string;
    onBack: () => void;
}

const API_KEY = process.env.REACT_APP_API_KEY || '';
const BASE_URL = process.env.REACT_APP_COMMUNICATION_API_URL;

// Funções auxiliares fora do componente para evitar redeclaração
const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência');
};

const getStatusColor = (status: string) => {
    const colors = {
        'PENDING': 'bg-slate-500',
        'SENT': 'bg-green-600',
        'DELIVERED': 'bg-green-700',
        'ERROR': 'bg-red-600',
        'FAILED': 'bg-red-700',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500';
};

const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
        'PENDING': 'Pendente',
        'SENT': 'Enviado',
        'DELIVERED': 'Entregue',
        'ERROR': 'Erro',
        'FAILED': 'Falhou',
    };
    return labels[status] || status;
};

const getRecipientIcon = (recipientType: string) => {
    return recipientType === 'WHATSAPP' ? 
        <FaWhatsapp className="text-green-500" size={20} /> : 
        <FaEnvelope className="text-blue-500" size={20} />;
};

const MessageDetailsComponent: React.FC<MessageDetailsProps> = ({ messageId, onBack }) => {
    const [message, setMessage] = useState<MessageDetails | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadMessageDetails();
    }, [messageId]);

    const loadMessageDetails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const headers: HeadersInit = {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json',
            };
            
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${BASE_URL}/message/${messageId}`, { headers });

            if (response.ok) {
                const data = await response.json();
                setMessage(data);
            } else {
                setMessage(null);
                toast.error('Erro ao carregar mensagem');
            }
        } catch (error) {
            console.error('Erro ao carregar detalhes da mensagem:', error);
            setMessage(null);
            toast.error('Erro ao carregar mensagem');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-white dark:bg-slate-800">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!message) {
        return (
            <div className="h-full flex flex-col items-center justify-center bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400">
                <FaExclamationTriangle size={32} className="mb-2 opacity-50" />
                <p>Mensagem não encontrada</p>
                <button
                    onClick={onBack}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Voltar
                </button>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-white dark:bg-slate-800">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-4">
                <div className="flex items-center space-x-3">
                    <button
                        onClick={onBack}
                        className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <FaArrowLeft size={16} />
                    </button>
                    <div className="flex items-center space-x-3">
                        {getRecipientIcon(message.recipientType)}
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Detalhes da Mensagem
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {message.recipient}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-6">
                    {/* Status */}
                    <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Status</h3>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white ${getStatusColor(message.status)}`}>
                                {getStatusLabel(message.status)}
                            </span>
                        </div>
                    </div>

                    {/* Informações Básicas */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-600 pb-2">
                            Informações Básicas
                        </h3>
                        
                        <div className="grid grid-cols-1 gap-4">
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID da Mensagem</p>
                                    <p className="text-sm font-mono text-gray-900 dark:text-white">{message.messageId}</p>
                                </div>
                                <button
                                    onClick={() => copyToClipboard(message.messageId)}
                                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                                    title="Copiar ID"
                                >
                                    <FaCopy size={14} />
                                </button>
                            </div>

                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID da Notificação</p>
                                    <p className="text-sm font-mono text-gray-900 dark:text-white">{message.notificationId}</p>
                                </div>
                                <button
                                    onClick={() => copyToClipboard(message.notificationId)}
                                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                                    title="Copiar ID"
                                >
                                    <FaCopy size={14} />
                                </button>
                            </div>

                            {message.externalId && (
                                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID Externo (SES)</p>
                                        <p className="text-sm font-mono text-gray-900 dark:text-white break-all">{message.externalId}</p>
                                    </div>
                                    <button
                                        onClick={() => copyToClipboard(message.externalId!)}
                                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                                        title="Copiar ID"
                                    >
                                        <FaCopy size={14} />
                                    </button>
                                </div>
                            )}

                            <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo de Notificação</p>
                                <p className="text-sm text-gray-900 dark:text-white">{message.notificationType}</p>
                            </div>

                            <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Operação</p>
                                <p className="text-sm text-gray-900 dark:text-white">{message.operation}</p>
                            </div>

                            <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data Agendada</p>
                                <p className="text-sm text-gray-900 dark:text-white">{message.scheduledDate}</p>
                            </div>

                            <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Destinatário</p>
                                <p className="text-sm text-gray-900 dark:text-white">{message.recipient}</p>
                            </div>

                            <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo</p>
                                <div className="flex items-center space-x-2 mt-1">
                                    {getRecipientIcon(message.recipientType)}
                                    <span className="text-sm text-gray-900 dark:text-white">
                                        {message.recipientType === 'WHATSAPP' ? 'WhatsApp' : 'Email'}
                                    </span>
                                </div>
                            </div>

                            <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assunto</p>
                                <p className="text-sm text-gray-900 dark:text-white">{message.subject}</p>
                            </div>

                            {message.isGrouped && (
                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Mensagem Agrupada</p>
                                    <p className="text-sm text-gray-900 dark:text-white">
                                        {message.groupedItemsCount} itens agrupados
                                    </p>
                                </div>
                            )}

                            {message.attachmentCount > 0 && (
                                <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Anexos</p>
                                    <p className="text-sm text-gray-900 dark:text-white">
                                        {message.attachmentCount} arquivo(s)
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-600 pb-2">
                            Timeline
                        </h3>
                        
                        <div className="space-y-3">
                            {message.createdAt && (
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                    <FaCheck className="text-gray-400" size={16} />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">Criado</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {message.createdAt}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {message.sentAt && (
                                <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <FaCheck className="text-green-500" size={16} />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">Enviado</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {message.sentAt}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {message.deliveredAt && (
                                <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <FaCheck className="text-green-600" size={16} />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">Entregue</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {message.deliveredAt}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {message.status === 'ERROR' && message.errorReason && (
                                <div className="flex items-start space-x-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                    <FaExclamationTriangle className="text-red-500 mt-0.5" size={16} />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">Erro</p>
                                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                            {message.errorReason}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {message.updatedAt && (
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                                    <FaCheck className="text-gray-400" size={16} />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">Última Atualização</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {message.updatedAt}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { MessageDetailsComponent as default };